import { useEffect, useMemo, useRef } from "react";
import { Link } from "react-router-dom";
import SeoBreadcrumbs from "../../components/automap/seo/SeoBreadcrumbs";
import { useClientViewSeo } from "../../clientViewRuntime/hooks/useClientViewSeo";
import {
  SELL_YOUR_CAR_FAQ,
  SELL_YOUR_CAR_FORM_SECTION_ID,
  SELL_YOUR_CAR_INFO_CHECKLIST,
  SELL_YOUR_CAR_SEARCH_HREF,
  SELL_YOUR_CAR_SELLER_TYPES,
  SELL_YOUR_CAR_STEPS,
  SELL_YOUR_CAR_UNAVAILABLE_COPY,
  SELL_YOUR_CAR_VALUE_CARDS,
} from "../../constants/sellYourCarPageConstants";
import { getSellYourCarSeo } from "../../seo/sellYourCarSeo";
import { SellerIntakeForm } from "../../components/automap/sellerIntake";
import { SELLER_SOURCE_ACTIONS, SELLER_SOURCE_COMPONENTS } from "../../utils/sellerIntake/sellerIntakeConstants";
import { isSellerIntakeEnabled } from "../../utils/sellerIntake/sellerIntakeConfig";
import {
  trackSellerIntakeCtaClick,
  trackSellerIntakePageView,
} from "../../utils/sellerIntake/sellerIntakeAnalytics";
import "../../styles/sellYourCarPage.css";

export default function SellYourCarPage() {
  const seo = useMemo(() => getSellYourCarSeo(), []);
  const pageViewTrackedRef = useRef(false);
  const sellerIntakeEnabled = isSellerIntakeEnabled();

  useClientViewSeo({
    title: seo.title,
    description: seo.description,
    canonicalUrl: seo.canonicalUrl,
    robots: seo.robots,
    openGraph: seo.openGraph,
    twitter: seo.twitter,
    structuredData: seo.structuredData,
    jsonLdId: "automap-sell-car-jsonld",
  });

  useEffect(() => {
    if (pageViewTrackedRef.current) return;
    pageViewTrackedRef.current = true;
    trackSellerIntakePageView({
      sourceComponent: SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FORM,
      sourceAction: SELLER_SOURCE_ACTIONS.SELLER_INTAKE_STARTED,
    });
  }, []);

  useEffect(() => {
    if (window.location.hash === `#${SELL_YOUR_CAR_FORM_SECTION_ID}`) {
      document.getElementById(SELL_YOUR_CAR_FORM_SECTION_ID)?.scrollIntoView({ behavior: "smooth" });
    }
  }, []);

  const handlePrimaryCtaClick = (sourceComponent) => {
    trackSellerIntakeCtaClick("primary", {
      sourceComponent,
      sourceAction: "scroll_to_form",
    });
  };

  const handleSecondaryCtaClick = (sourceComponent) => {
    trackSellerIntakeCtaClick("secondary", {
      sourceComponent,
      sourceAction: "browse_listings",
    });
  };

  return (
    <div className="sell-car-page">
      <header className="sell-car-page__hero">
        <div className="sell-car-page__hero-inner">
          <SeoBreadcrumbs items={seo.breadcrumbs} />
          <span className="sell-car-page__eyebrow">AutoMapa · Vender</span>
          <h1>Vende tu carro con más confianza</h1>
          <p className="sell-car-page__hero-lead">
            Comparte la información de tu vehículo y el equipo de AutoMapa revisará los datos para
            ayudarte a preparar una publicación clara, útil y confiable.
          </p>
          <div className="sell-car-page__cta-row">
            <a
              className="sell-car-page__btn sell-car-page__btn--primary"
              href={`#${SELL_YOUR_CAR_FORM_SECTION_ID}`}
              onClick={() => handlePrimaryCtaClick(SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_HERO_CTA)}
            >
              Enviar mi carro para revisión
            </a>
            <Link
              className="sell-car-page__btn sell-car-page__btn--secondary"
              to={SELL_YOUR_CAR_SEARCH_HREF}
              onClick={() => handleSecondaryCtaClick(SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_HERO_CTA)}
            >
              Ver carros en venta
            </Link>
          </div>
        </div>
      </header>

      <main className="sell-car-page__main">
        <section className="sell-car-page__section" aria-labelledby="sell-car-value-heading">
          <h2 id="sell-car-value-heading">Por qué enviar tu vehículo a AutoMapa</h2>
          <div className="sell-car-page__cards">
            {SELL_YOUR_CAR_VALUE_CARDS.map((card) => (
              <article key={card.title} className="sell-car-page__card">
                <h3>{card.title}</h3>
                <p>{card.copy}</p>
              </article>
            ))}
          </div>
        </section>

        <section className="sell-car-page__section" aria-labelledby="sell-car-steps-heading">
          <h2 id="sell-car-steps-heading">Cómo funciona</h2>
          <ol className="sell-car-page__steps">
            {SELL_YOUR_CAR_STEPS.map((step, index) => (
              <li key={step}>
                <strong>Paso {index + 1}.</strong> {step}
              </li>
            ))}
          </ol>
          <p>
            Este proceso nos ayuda a mantener publicaciones más claras y útiles para compradores reales.
          </p>
        </section>

        <section className="sell-car-page__section" aria-labelledby="sell-car-info-heading">
          <h2 id="sell-car-info-heading">Qué información necesitarás</h2>
          <p>Completa el formulario más abajo para compartir:</p>
          <ul className="sell-car-page__checklist">
            {SELL_YOUR_CAR_INFO_CHECKLIST.map((item) => (
              <li key={item.label}>
                <span>{item.label}</span>
                {item.note ? <span className="sell-car-page__badge">{item.note}</span> : null}
              </li>
            ))}
          </ul>
          <p className="sell-car-page__privacy-note">
            Datos sensibles como placa o VIN, si se solicitan más adelante, se manejarán de forma
            privada y solo para revisión interna.
          </p>
        </section>

        <section className="sell-car-page__section" aria-labelledby="sell-car-sellers-heading">
          <h2 id="sell-car-sellers-heading">¿Quién puede usar este flujo?</h2>
          <p>
            Este es un flujo de envío y revisión — no publicación instantánea. Puede servir para:
          </p>
          <ul className="sell-car-page__seller-types">
            {SELL_YOUR_CAR_SELLER_TYPES.map((type) => (
              <li key={type}>{type}</li>
            ))}
          </ul>
        </section>

        <section className="sell-car-page__section sell-car-page__notice" aria-labelledby="sell-car-safety-heading">
          <h2 id="sell-car-safety-heading">Primero revisamos la información</h2>
          <p>
            Para proteger la calidad del marketplace, las publicaciones pueden pasar por una revisión
            antes de aparecer en AutoMapa. Esto ayuda a evitar datos incompletos, duplicados o
            publicaciones poco confiables.
          </p>
          <p>
            AutoMapa no certifica el estado del vehículo ni garantiza una venta. La revisión busca
            mejorar claridad y confianza para compradores y vendedores.
          </p>
        </section>

        <section
          id={SELL_YOUR_CAR_FORM_SECTION_ID}
          className="sell-car-page__section sell-car-page__form"
          aria-label="Formulario de venta"
        >
          {sellerIntakeEnabled ? (
            <SellerIntakeForm />
          ) : (
            <div className="sell-car-page__unavailable" role="status">
              <h2>{SELL_YOUR_CAR_UNAVAILABLE_COPY.title}</h2>
              <p>{SELL_YOUR_CAR_UNAVAILABLE_COPY.body}</p>
              <Link className="sell-car-page__btn sell-car-page__btn--secondary" to={SELL_YOUR_CAR_SEARCH_HREF}>
                Ver carros en venta
              </Link>
            </div>
          )}
        </section>

        <section className="sell-car-page__section" aria-labelledby="sell-car-faq-heading">
          <h2 id="sell-car-faq-heading">Preguntas frecuentes</h2>
          <div className="sell-car-page__faq">
            {SELL_YOUR_CAR_FAQ.map((item) => (
              <details key={item.question}>
                <summary>{item.question}</summary>
                <p>{item.answer}</p>
              </details>
            ))}
          </div>
        </section>

        <section className="sell-car-page__section sell-car-page__final-cta" aria-labelledby="sell-car-final-heading">
          <h2 id="sell-car-final-heading">¿Listo para el siguiente paso?</h2>
          <p>Completa el formulario para enviar tu vehículo a revisión desde esta misma página.</p>
          <div className="sell-car-page__cta-row">
            <a
              className="sell-car-page__btn sell-car-page__btn--primary"
              href={`#${SELL_YOUR_CAR_FORM_SECTION_ID}`}
              onClick={() => handlePrimaryCtaClick(SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FINAL_CTA)}
            >
              Enviar mi carro para revisión
            </a>
            <Link
              className="sell-car-page__btn sell-car-page__btn--secondary"
              to={SELL_YOUR_CAR_SEARCH_HREF}
              onClick={() => handleSecondaryCtaClick(SELLER_SOURCE_COMPONENTS.SELL_YOUR_CAR_FINAL_CTA)}
            >
              Ver carros en venta
            </Link>
          </div>
        </section>
      </main>
    </div>
  );
}
